import React, { useState } from "react";
import clsx from "clsx";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import FacilityMarkerIndicator from "components/FacilityMarkerIndicator";
import Loader from "components/Loader";
import { useLoggedInUser } from "components/LoggedInUserProvider";
import { useRacehorse360Api } from "hooks/api";
import { getTimeAgo } from "utils/date-utils";
import {
  listCoursesGetOptions,
  conditionsLabels,
  FacilityCourses
} from "../constants";
import { getRelatedFacilities } from "../utils";

import useStyles from "./styles";

export const LIST_ALL_ACTIVE_COURSES_QUERY_KEY = "allActiveCourses";

const AllCoursesList = () => {
  const classes = useStyles();

  const [ListCourses, setListCourses] = useState<FacilityCourses>({});
  const [coursesIsLoading, setCoursesIsLoading] = useState<boolean>(true);

  const { useListCourses } = useRacehorse360Api();
  const {
    currentUser: { isMaintenance, isRegulatoryVet, isTrainer, isTrackVet }
  } = useLoggedInUser();
  const shouldShowPopupView = [
    isMaintenance,
    isRegulatoryVet,
    isTrainer,
    isTrackVet
  ].some(item => item);

  useListCourses(
    "allActiveCourses",
    {
      query: {
        facilityIds: [],
        isActive: {
          value: true
        },
        isFacilityActive: {
          value: true
        }
      },
      getOptions: {
        select: listCoursesGetOptions
      },
      pagingOptions: {
        maxResults: 1000
      }
    },
    {
      onSuccess: result => {
        setListCourses(getRelatedFacilities(result));
        setCoursesIsLoading(false);
      },
      onError: error => {
        console.error(error);
      }
    }
  );

  if (coursesIsLoading) return <Loader className={classes.loader} />;

  if (!Object.keys(ListCourses).length) {
    return (
      <div className={classes.noDataHolder}>
        <div className={classes.noDataHolderText}>No Data to Display</div>
      </div>
    );
  }

  const renderFacilityCourses = (courses: racehorse360.ICourse[]) => {
    return courses.map(course => {
      const { id, name, comment, trackCondition, updatedOn } = course;

      return (
        <div className={classes.courseInfoContainer} key={id}>
          <div className={classes.courseInfo}>
            <div className={classes.courseCondition}>
              <span className={classes.courseName}>{`${name}: `}</span>
              <span className={classes.conditionTitle}>
                {conditionsLabels[trackCondition] || "-"}
              </span>
            </div>
            <div className={classes.lastUpdated}>{getTimeAgo(updatedOn)}</div>
          </div>
          <div className={classes.courseComment}>{comment}</div>
        </div>
      );
    });
  };

  const renderFacilities = () => {
    return Object.keys(ListCourses).map(it => {
      const { courses = [], facility = {} } = ListCourses[it];
      const { id, name, code } = facility;

      return (
        <div className={classes.allCoursesListItem} key={id}>
          <div className={classes.listItemHeader}>
            <div className={classes.listItemHeaderTitle}>
              <div className={classes.marker}>
                <FacilityMarkerIndicator facility={facility} size={6} />
              </div>
              <span className={classes.facilityName}>{`${name} (${
                code || "-"
              })`}</span>
            </div>
            <div className={classes.listItemHeaderLastChanged}>Last Change</div>
          </div>
          <div className={classes.listItemBody}>
            {renderFacilityCourses(courses)}
          </div>
        </div>
      );
    });
  };

  return (
    <div
      className={clsx(classes.allCoursesList, {
        popupView: shouldShowPopupView
      })}
    >
      {renderFacilities()}
    </div>
  );
};

export default AllCoursesList;
